import {
  add,
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfSecond,
  startOfWeek,
  startOfYear,
  sub
} from 'date-fns';

import { assert } from 'cadenza/utils/custom-error';
import { CONDITION_SETTINGS } from 'cadenza/workbook/condition-edit-dialog/condition-settings';

/**
 * Possible units to specify steps in which to step through the temporal data. The values are
 * directly usable as keys for a date-fns {@link Duration}, and also have a corresponding value in
 * java.time.temporal.ChronoUnit which is used in the conditionConfiguration.sliderConfiguration
 * coming from the server, but with the ChronoUnits name starting with a captial letter.
 *
 * Corresponding java enum: net.disy.cadenza.workbook.resolving.condition.TickUnit
 * Corresponding TypeScript type:
 * {@link import('cadenza/conditions/date-time-condition/tick-unit').TickUnit}
 *
 * @enum {string}
 * @readonly
 */
export const TickUnit = {
  YEARS: 'years',
  MONTHS: 'months',
  WEEKS: 'weeks',
  DAYS: 'days',
  HOURS: 'hours',
  MINUTES: 'minutes',
  SECONDS: 'seconds'
};

export const TickUnitOrder = {
  [TickUnit.SECONDS]: 0,
  [TickUnit.MINUTES]: 1,
  [TickUnit.HOURS]: 2,
  [TickUnit.DAYS]: 3,
  [TickUnit.MONTHS]: 4,
  [TickUnit.YEARS]: 5
};

export const TickUnitToDateFormatUnit = {
  [TickUnit.SECONDS]: 's',
  [TickUnit.MINUTES]: 'm',
  [TickUnit.HOURS]: 'H',
  [TickUnit.DAYS]: 'd',
  [TickUnit.MONTHS]: 'M',
  [TickUnit.YEARS]: 'y'
};

export const StepDirection = {
  NEXT: 'next',
  PREVIOUS: 'previous'
};

const RelativeDateUnitToTickUnit = {
  'DAY': TickUnit.DAYS,
  'WEEK': TickUnit.WEEKS,
  'MONTH': TickUnit.MONTHS,
  'QUARTER': TickUnit.MONTHS,
  'YEAR': TickUnit.YEARS
};
/**
 * Checks whether the steps configuration is compatible with the condition value range and adjust
 * the steps configuration to fit the values.
 * If possible, only the window size is adjusted and the step unit is left as is, but if the value
 * range does not fit exactly into a multiple of the unit, the unit is adjusted, too (set to the
 * fallback value).
 *
 * @param {Date} startDate - date range start value
 * @param {Date} endDate - date range end value
 * @param {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} currentStepsConfig
 *   - the current steps configuration
 * @param {TickUnit} smallestStepUnit - step unit to fall back to if the passed date range is not compatible with the step unit in currentStepsConfig
 * @return {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} - the
 *   adapted steps config (can be equal to the one passed in if no changes were necessary)
 */
export function adaptStepsConfigToConditionValue (startDate, endDate, currentStepsConfig, smallestStepUnit) {
  assert(startDate, 'startDate must be defined');
  assert(endDate, 'endDate must be defined');
  assert(currentStepsConfig, 'currentStepsConfig must be defined');
  assert(smallestStepUnit, 'smallestStepUnit must be defined');

  // We ignore more fine-grained date/time components than the smallestStepUnit
  startDate = roundToTickUnit(startDate, smallestStepUnit);
  const roundedEndDate = roundToTickUnit(endDate, smallestStepUnit);
  const adjustedEndDate = addTickUnits(roundedEndDate, 1, smallestStepUnit);

  const rangeLengthInStepUnit = differenceInTickUnit(startDate, adjustedEndDate, currentStepsConfig.unit);
  const lenghInStepUnitsCoversFullRange =
    addTickUnits(startDate, rangeLengthInStepUnit, currentStepsConfig.unit).getTime()
      === adjustedEndDate.getTime();

  if (lenghInStepUnitsCoversFullRange) {
    return {
      ...currentStepsConfig,
      timeWindow: rangeLengthInStepUnit
    };
  } else {
    const timeWindow = differenceInTickUnit(startDate, adjustedEndDate, smallestStepUnit);
    return {
      ...currentStepsConfig,
      unit: smallestStepUnit,
      timeWindow
    };
  }
}

/**
 * In case the steps config changed, it might be necessary to adapt the condition value to fit the
 * new steps config.
 *
 * @param {object} dateConditionRangeValue
 * @param {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} stepsConfig
 * @return {object} - the adapted date range (can be equal to the one passed in if no changes were
 *   necessary)
 */
export function adaptConditionValueToStepsConfig (dateConditionRangeValue, stepsConfig) {
  if (dateConditionRangeValue) {
    const newEndDate = addTickUnits(
      new Date(dateConditionRangeValue.start),
      stepsConfig.timeWindow,
      stepsConfig.unit);
    return {
      ...dateConditionRangeValue,
      end: newEndDate.toISOString()
    };
  } else {
    return { ...dateConditionRangeValue };
  }
}

/**
 * Calculates the difference of two dates in the specified unit. Always returns an integer.
 * Fractions of the the specified unit that additionally fit into the date range are discarded.
 *
 * @param {Date} start - start date
 * @param {Date} end - end date
 * @param {TickUnit} tickUnit - unit
 * @return {number} the rounded-down difference between start and end, in the specified unit
 */
export function differenceInTickUnit (start, end, tickUnit) {
  switch (tickUnit) {
    case TickUnit.YEARS:   return differenceInYears(end, start);
    case TickUnit.MONTHS:  return differenceInMonths(end, start);
    case TickUnit.WEEKS:   return differenceInWeeks(end, start);
    case TickUnit.DAYS:    return differenceInDays(end, start);
    case TickUnit.HOURS:   return differenceInHours(end, start);
    case TickUnit.MINUTES: return differenceInMinutes(end, start);
    case TickUnit.SECONDS: return differenceInSeconds(end, start);
  }
}

/**
 * Rounds the given date to the given date/time unit
 *
 * @param {Date} date - date to round
 * @param {TickUnit} tickUnit - unit to round to
 * @return {Date} rounded date
 */
export function roundToTickUnit (date, tickUnit) {
  switch (tickUnit) {
    case TickUnit.YEARS:   return startOfYear(date);
    case TickUnit.MONTHS:  return startOfMonth(date);
    case TickUnit.WEEKS:   return startOfWeek(date);
    case TickUnit.DAYS:    return startOfDay(date);
    case TickUnit.HOURS:   return startOfHour(date);
    case TickUnit.MINUTES: return startOfMinute(date);
    case TickUnit.SECONDS: return startOfSecond(date);
  }
}

/**
 * Adds time to a date.
 *
 * @param {Date} date - base date
 * @param {number} number - number to be added
 * @param {TickUnit} tickUnit - unit the number is interpreted as
 * @return {Date} - base date plus added time
 */
export function addTickUnits (date, number, tickUnit) {
  switch (tickUnit) {
    case TickUnit.YEARS:   return addYears(date, number);
    case TickUnit.MONTHS:  return addMonths(date, number);
    case TickUnit.WEEKS:   return addWeeks(date, number);
    case TickUnit.DAYS:    return addDays(date, number);
    case TickUnit.HOURS:   return addHours(date, number);
    case TickUnit.MINUTES: return addMinutes(date, number);
    case TickUnit.SECONDS: return addSeconds(date, number);
  }
}

export function stepsConfigEquals (stepConfig1, stepConfig2) {
  if ((stepConfig1 ?? null) === null) {
    return stepConfig1 === stepConfig2;
  }

  return stepConfig1.timeWindow === stepConfig2.timeWindow
    && stepConfig1.unit === stepConfig2.unit
    && stepConfig1.overlap === stepConfig2.overlap;
}

export function dateConditionRangeValueEquals (conditionValue1, conditionValue2) {
  if ((conditionValue1 ?? null) === null) {
    return conditionValue1 === conditionValue2;
  }

  return conditionValue1.start === conditionValue2.start
    && conditionValue1.end === conditionValue2.end;
}

/**
 * Creates a StepsConfig object from an object containing condition settings (keys from
 * CONDITION_SETTINGS).
 *
 * @param {object} conditionSettings
 * @return {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} the
 *   steps config
 */
export function toStepsConfig (conditionSettings) {
  return {
    timeWindow: conditionSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH],
    unit: conditionSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT],
    overlap: conditionSettings[CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH]
  };
}

export function toConditionSettings (stepsConfig) {
  return {
    [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH]: stepsConfig.timeWindow,
    [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT]: stepsConfig.unit,
    [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH]: stepsConfig.overlap
  };
}

export function mostFineGrainedDateFormatTickUnit (format = '') {
  let keys = Object.keys(TickUnitToDateFormatUnit);
  keys = keys.sort((a, b) => TickUnitOrder[a] - TickUnitOrder[b]);
  return keys.find((key) => {
    return format.includes(TickUnitToDateFormatUnit[key]);
  });
}

export function mostFineGrainedTickUnit (dateConfiguration) {
  if (dateConfiguration.secondEnabled) {
    return TickUnit.SECONDS;
  }
  if (dateConfiguration.minuteEnabled) {
    return TickUnit.MINUTES;
  }
  if (dateConfiguration.hourEnabled) {
    return TickUnit.HOURS;
  }
  if (dateConfiguration.dayEnabled) {
    return TickUnit.DAYS;
  }
  if (dateConfiguration.monthEnabled) {
    return TickUnit.MONTHS;
  }
  if (dateConfiguration.yearEnabled) {
    return TickUnit.YEARS;
  }
  throw new Error('At least one time unit must be enabled');
}

/**
 * Creates a steps configuration according to the relative date that is passed.
 *
 * @param {object} relativeDate - Relative date to create the steps config from.
 * @param {string} relativeDate.unit - Date unit, Corresponding java enum: net.disy.cadenza.workbook.resolving.condition.TickUnit
 * @param {number} relativeDate.amount - 1, 0 or -1 (next, this or last [unit])
 * @return {{unit: string, timeWindow: (number), overlap: number}}
 */
export function stepsConfigFromRelativeDate (relativeDate) {
  return {
    unit: RelativeDateUnitToTickUnit[relativeDate.unit],
    timeWindow: relativeDate.unit === 'QUARTER' ? 3 : 1,
    overlap: 0
  };
}

/**
 * @typedef {object} CalculateSteppedSliderRangeResult
 * @property {object} sliderRange - Slider range that is possibly non-linear to use with NoUiSlider @see {@link https://refreshless.com/nouislider/examples/#section-non-linear}
 * @property {number} lastLowerHandleTickValue - The maximum tick value that is applicable, slider can't be dragged beyond that.
 */

/**
 * In stepping mode the slider can only be moved to certain values on the slider track. Therefore
 * the allowed values are calculated and returned as needed by NoUiSlider. Also the maximum last
 * tick value for the slider is returned.
 *
 * @param {number} stepSize - Time window step size.
 * @param {TickUnit} tickUnit - Smallest available date unit granularity.
 * @param {number} lastTickValue - End of date range as tick value.
 * @param {object} dateRange - Maximum available date range.
 * @param {TickUnit} stepsUnit - Time window date unit.
 * @param {object} conditionStartValue - Current condition start date string, needed to calculate a slider range that includes that value.
 * @param {boolean} isRelativeDate - Whether the conditionValue is derived from a relative date.
 * @return {CalculateSteppedSliderRangeResult}
 */
export function calculateSteppedSliderRange (
  stepSize,
  tickUnit,
  lastTickValue,
  dateRange,
  stepsUnit,
  conditionStartValue,
  isRelativeDate
) {
  conditionStartValue = conditionStartValue ?? dateRange.lowerBound;
  conditionStartValue = isRelativeDate
    ? relativeConditionValueToStartDate(conditionStartValue, dateRange, isRelativeDate, stepsUnit, stepSize)
    : new Date(conditionStartValue);
  stepSize = Math.abs(stepSize); // Negative stepSize would break the slider.
  const adjustedDateRange = {
    lowerBound: calculateSteppedSliderRangeStart(stepsUnit, stepSize, conditionStartValue ?? dateRange.lowerBound, dateRange.lowerBound),
    upperBound: dateRange.upperBound
  };
  const sliderRange = {
    max: [ lastTickValue ]
  };
  const getNextPossibleIndex = (range, index, desiredStepSize) => {
    const desiredIndex = index + desiredStepSize;
    if (range.length > desiredIndex) {
      return desiredIndex;
    } else if (range.length > index) {
      return range.length - 1;
    } else {
      return index;
    }
  };

  const offset = differenceInTickUnit(dateRange.lowerBound, adjustedDateRange.lowerBound, tickUnit);
  const slicedDateRange = sliceDateRangeByTickUnit(adjustedDateRange, stepsUnit);
  let tickValue;
  slicedDateRange.forEach((date, index) => {
    if (index % stepSize === 0) {
      tickValue = differenceInTickUnit(dateRange.lowerBound, date, tickUnit);
      const tickSliderPosition = (tickValue - offset) * 100 / (sliderRange.max - offset);
      // For more information on how noUi-Slider supports stepping with non-linear sliders see
      // https://refreshless.com/nouislider/slider-values/#section-non-linear-step
      if (index < slicedDateRange.length) {
        const nextIndex = getNextPossibleIndex(slicedDateRange, index, stepSize);
        const nextValue = differenceInTickUnit(dateRange.lowerBound, slicedDateRange[nextIndex], tickUnit);
        if (index === 0) {
          // Special case lowest value in range: field must be named 'min'.
          sliderRange.min = [ offset, nextValue - tickValue ];
        } else {
          // For each step the slider has, the following step size is also set. That's the condition
          // that must be met for a non-linear slider to be in stepping mode.
          sliderRange[`${tickSliderPosition}%`] = [ tickValue, nextValue - tickValue ];
        }
      }
    }
  });
  return { sliderRange, lastLowerHandleTickValue: tickValue };
}

/**
 * Determines if stepping on the slider is possible for the given direction when a relative date is set.
 *
 * @param {StepDirection} direction - Which stepping direction should be tested: previous or next.
 * @param {number} sliderTickValue - The relative date to test in tick values.
 * @param {number} minRangeLimit - The lower range limit in tick values.
 * @param {number} maxRangeLimit - The upper range limit in tick values.
 * @return {boolean} - If stepping in the given direction is possible.
 */
export function canStepFromRelativeDate (direction, sliderTickValue, minRangeLimit, maxRangeLimit) {
  if (sliderTickValue > minRangeLimit && sliderTickValue < maxRangeLimit) {
    return true;
  }
  return (direction === StepDirection.PREVIOUS && sliderTickValue > maxRangeLimit)
    || (direction === StepDirection.NEXT && sliderTickValue < minRangeLimit);
}

// Calculate the start date if the relative date is out of date range bounds.
function relativeConditionValueToStartDate (conditionStartValue, dateRange, isRelativeDate, stepsUnit, stepSize) {
  const lowerBound = dateRange.lowerBound;
  const upperBound = dateRange.upperBound;
  let start = new Date(conditionStartValue);

  if ((isAfter(start, lowerBound) || isEqual(start, lowerBound))
    && (isBefore(start, upperBound) || isEqual(start, upperBound))) {
    return start; // start is within date range
  }

  // step forward towards the allowed range
  if (isBefore(start, lowerBound)) {
    while (isBefore(start, lowerBound)) {
      start = add(start, { [stepsUnit]: stepSize });
    }
    if (isAfter(start, upperBound)) {
      return lowerBound; // No match in the given date range, return lower bound.
    }
    return start;
  }

  // step backwards towards the allowed range
  if (isAfter(start, upperBound)) {
    while (isAfter(start, upperBound)) {
      start = add(start, { [stepsUnit]: -stepSize });
    }
    if (isBefore(start, lowerBound)) {
      return lowerBound; // No match in the given date range, return lower bound.
    }

    return start;
  }

  return lowerBound; // Fallback: return lower bound.
}

function calculateSteppedSliderRangeStart (stepsUnit, stepSize, conditionStartValue, dateRangeLowerBound) {
  let rangeStart = new Date(conditionStartValue);
  let newRangeStart = rangeStart;
  while (isAfter(newRangeStart, dateRangeLowerBound)) {
    newRangeStart = sub(rangeStart, { [stepsUnit]: stepSize });
    if (!isBefore(newRangeStart, dateRangeLowerBound)) {
      rangeStart = newRangeStart;
    }
  }
  return rangeStart;
}

function sliceDateRangeByTickUnit (dateRange, unit) {
  const dates = [];
  let nextDate = dateRange.lowerBound;
  while (nextDate < dateRange.upperBound) {
    dates.push(nextDate);
    nextDate = add(nextDate, { [unit]: 1 });
  }
  return dates;
}
